import { useForm } from 'react-hook-form';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { IoTrendingDownOutline } from 'react-icons/io5';
import { MdSentimentNeutral, MdSentimentSatisfiedAlt, MdSentimentVeryDissatisfied } from 'react-icons/md';
import { useGetSignals, useGetSentimentSummary } from '@/apis/business/agent/signals/hook';
import { useGetAgent } from '@/apis/business/agent/hook';
import SignalsHeader from './components/SignalsHeader';
import SignalsListing from './components/SignalsListing';
import SignalsInsights from './components/SignalsInsights';
import KeywordSettingsDrawer from './components/KeywordSettingsDrawer';
import { FilterFormData } from './components/types';
import { usePagination } from '@/components/Common/Pagination';

const INSIGHTS_MIN_DATE = dayjs('2026-05-01', 'YYYY-MM-DD', true);

const getPastMonthDateRange = (clampToInsightsMinDate = false) => {
  const today = dayjs().startOf('day');
  const startDate = today.subtract(1, 'month');
  const effectiveStartDate =
    clampToInsightsMinDate && startDate.isBefore(INSIGHTS_MIN_DATE, 'day') ? INSIGHTS_MIN_DATE : startDate;

  return `${effectiveStartDate.format('YYYY-MM-DD')},${today.format('YYYY-MM-DD')}`;
};

const DEFAULT_KEYWORD_FILTER_OPTIONS = [
  'Long Wait',
  'No Callback',
  'Check-In Experience',
  'Missed Appointment',
  'Paperwork',
  'Emergency',
  'Connect to Human',
  'Negative Review Threat',
  'Rude Staff',
  'Morning Slots',
  'Noon Slots',
  'Evening Slots'
];

const defaultFormValues: FilterFormData = {
  persona_eq: 'all',
  sentiment: 'all',
  severity: 'all',
  keywords: 'all',
  signal_state: 'active',
  date: getPastMonthDateRange()
};

const getInitialFormValues = (searchParams: URLSearchParams): FilterFormData => {
  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const keywords = searchParams.get('keywords');

  if (startDate && endDate) {
    return { ...defaultFormValues, date: `${startDate},${endDate}`, keywords: keywords || 'all' };
  }

  return { ...defaultFormValues, keywords: keywords || 'all' };
};

export default function Signals() {
  const theme = useTheme();
  const { data: agent } = useGetAgent();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskCompletedFilter = searchParams.get('task_completed');
  const workflowFilter = searchParams.get('workflow');
  const hasDeepLinkedDates = Boolean(searchParams.get('start_date') && searchParams.get('end_date'));
  const viewMode: 'list' | 'insights' = searchParams.get('view') === 'insights' ? 'insights' : 'list';
  const initialFormValues = useMemo(() => getInitialFormValues(searchParams), [searchParams]);
  const { control, watch, setValue } = useForm<FilterFormData>({
    defaultValues: initialFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleViewModeChange = (mode: 'list' | 'insights') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'insights') next.set('view', 'insights');
        else next.delete('view');
        return next;
      },
      { replace: true }
    );
  };

  const transformFilters = useCallback(
    (formData: FilterFormData) => {
      const filters: Record<string, any> = { ...formData };
      const dateArr = filters.date?.split(',');
      if (dateArr?.[0]) filters['filter[created_at_gteq]'] = dateArr[0];
      if (dateArr?.[1]) filters['filter[created_at_lteq]'] = dateArr[1];

      if (filters.persona_eq !== 'all') filters['filter[persona_eq]'] = filters.persona_eq;
      if (filters.sentiment !== 'all') filters['filter[sentiment_eq]'] = filters.sentiment;
      if (filters.severity !== 'all') filters['filter[severity_eq]'] = filters.severity;
      if (filters.keywords && filters.keywords !== 'all') filters['filter[keywords_eq]'] = filters.keywords;

      if (formData.signal_state === 'active') {
        filters['filter[discarded_at_null]'] = true;
      } else if (formData.signal_state === 'archived') {
        filters['filter[discarded_at_not_null]'] = true;
      }

      if (taskCompletedFilter) filters['filter[task_completed_eq]'] = taskCompletedFilter;
      if (workflowFilter) filters['filter[workflow_eq]'] = workflowFilter;

      delete filters.date;
      delete filters.persona_eq;
      delete filters.sentiment;
      delete filters.severity;
      delete filters.keywords;
      delete filters.signal_state;

      return filters;
    },
    [taskCompletedFilter, workflowFilter]
  );

  const watchedValues = watch();
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>(() => transformFilters(initialFormValues));
  const prevViewModeRef = useRef<'list' | 'insights'>(viewMode);
  const skipInitialViewModeDateResetRef = useRef(hasDeepLinkedDates);

  const transformedFilters = useMemo(() => transformFilters(watchedValues), [watchedValues, transformFilters]);

  useEffect(() => {
    if (prevViewModeRef.current === viewMode) return;

    prevViewModeRef.current = viewMode;

    if (skipInitialViewModeDateResetRef.current) {
      skipInitialViewModeDateResetRef.current = false;
      return;
    }

    setValue('date', getPastMonthDateRange(viewMode === 'insights'));
  }, [viewMode, setValue]);

  useEffect(() => {
    setAppliedFilters((prevFilters) => {
      const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(transformedFilters);
      if (filtersChanged) {
        resetPage();
        return transformedFilters;
      }
      return prevFilters;
    });
  }, [transformedFilters, resetPage]);

  useEffect(() => {
    if (!(taskCompletedFilter || workflowFilter)) return;
    resetPage();
  }, [taskCompletedFilter, workflowFilter, resetPage]);

  const apiParams = useMemo(
    () => ({ ...appliedFilters, page: currentPage, per_page: perPage }),
    [appliedFilters, currentPage, perPage]
  );

  const dateValue = watchedValues.date || defaultFormValues.date;
  const [startDate, endDate] = dateValue.split(',');
  const dateParams = useMemo(() => ({ start_date: startDate, end_date: endDate }), [startDate, endDate]);

  const { data: SignalsData, isLoading } = useGetSignals(apiParams);
  const { data: sentimentSummary, isLoading: isSentimentSummaryLoading } = useGetSentimentSummary(dateParams);

  const keywordOptions = useMemo(() => {
    const customNames = (agent?.custom_keywords ?? [])
      .map((item) => item.keyword?.trim())
      .filter((keyword): keyword is string => Boolean(keyword));

    return Array.from(new Set([...DEFAULT_KEYWORD_FILTER_OPTIONS, ...customNames]));
  }, [agent?.custom_keywords]);

  useEffect(() => {
    if (watchedValues.keywords === 'all') return;
    if (!keywordOptions.includes(watchedValues.keywords)) {
      setValue('keywords', 'all');
    }
  }, [keywordOptions, watchedValues.keywords, setValue]);

  const metricCards = useMemo(() => {
    const s = sentimentSummary || {};
    return [
      {
        key: 'satisfied',
        title: 'Satisfied',
        value: s['Satisfied'] || s['Positive'] || 0,
        icon: <MdSentimentSatisfiedAlt size={18} color={theme.palette.primary.main} />
      },
      {
        key: 'neutral',
        title: 'Neutral',
        value: s['Neutral'] || s['Mixed'] || 0,
        icon: <MdSentimentNeutral size={18} color={theme.palette.primary.main} />
      },
      {
        key: 'frustrated',
        title: 'Frustrated',
        value: s['Frustrated'] || s['Challenging'] || 0,
        icon: <MdSentimentVeryDissatisfied size={18} color={theme.palette.primary.main} />
      },
      {
        key: 'churn_risk',
        title: 'Churn Risk',
        value: s['Churn Risk'] || s['Extremely Negative'] || 0,
        icon: <IoTrendingDownOutline size={18} color={theme.palette.primary.main} />
      }
    ];
  }, [sentimentSummary, theme.palette.primary.main]);

  return (
    <>
      <SignalsHeader
        control={control}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onOpenSettings={() => setIsSettingsOpen(true)}
        metricCards={metricCards}
        isMetricsLoading={isSentimentSummaryLoading}
        keywordOptions={keywordOptions}
        createdAt={agent?.created_at}
      />

      {viewMode === 'list' ? (
        <SignalsListing
          data={SignalsData?.data || []}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={SignalsData?.meta?.total_pages || 1}
          totalCount={SignalsData?.meta?.total_count ?? 0}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      ) : (
        <SignalsInsights startDate={startDate} endDate={endDate} />
      )}

      <KeywordSettingsDrawer open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      <Outlet />
    </>
  );
}
